<template>
    <div v-if="showQuadrants">
        <div class="d-flex">
            <div class="axis axis__y">
                <i class="axis__icon fas fa-arrow-up" />
                <span class="axis__title">
                    {{ axisYTitle }}
                </span>
            </div>
            <div class="w-100">
                <AxisLimitsValidations
                    class="mb-2"
                    :itemsValidations="limitsValidations.y"
                />
                <div class="quadrants">
                    <b-row
                        :key="`${column}-${y}`"
                        v-for="(column, y) in columns"
                    >
                        <QuadrantsLimits
                            :key="`limits-${column}-${y}`"
                            type="column"
                            class="axis-y-limits"
                            :showMinInput="column == columns - (columns - 1)"
                            :minValue.sync="limits.y[0]"
                            :maxValue.sync="limits.y[column]"
                            :minValueState="getLimitValidation('y', 0)"
                            :maxValueState="getLimitValidation('y', column)"
                            @update="
                                (prop, value) =>
                                    onUpdateLimit(
                                        prop == 'minValue' ? 0 : column,
                                        'y',
                                        value
                                    )
                            "
                        />
                        <b-col :key="`${row}-${x}`" v-for="(row, x) in rows">
                            <template
                                v-for="index in [getQuadrantIndex(row, column)]"
                            >
                                <div
                                    v-if="index !== -1"
                                    :key="quadrants[index].id"
                                >
                                    <Quadrant
                                        :title="quadrants[index].title"
                                        :description="
                                            quadrants[index].description
                                        "
                                        :validations="{
                                            title:
                                                quadrantsValidations[index]
                                                    .title,
                                            description:
                                                quadrantsValidations[index]
                                                    .description,
                                        }"
                                        @update="
                                            (prop, value) =>
                                                updateQuadrantProperty(
                                                    index,
                                                    prop,
                                                    value
                                                )
                                        "
                                    />
                                    <QuadrantsLimits
                                        :key="`${rows}-${x}`"
                                        v-if="column == columns - (columns - 1)"
                                        type="row"
                                        style="margin-top: 15px"
                                        :minValue.sync="limits.x[0]"
                                        :maxValue.sync="limits.x[row]"
                                        :minValueState="
                                            getLimitValidation('x', 0)
                                        "
                                        :maxValueState="
                                            getLimitValidation('x', row)
                                        "
                                        :showMinInput="row == rows - (rows - 1)"
                                        @update="
                                            (prop, value) =>
                                                onUpdateLimit(
                                                    prop == 'minValue'
                                                        ? 0
                                                        : row,
                                                    'x',
                                                    value
                                                )
                                        "
                                    />
                                </div>
                            </template>
                        </b-col>
                    </b-row>
                </div>
                <AxisLimitsValidations
                    textRight
                    :itemsValidations="limitsValidations.x"
                />
            </div>
        </div>
        <div class="axis axis__x">
            <span class="axis__title">{{ axisXTitle }}</span>
            <i class="axis__icon fas fa-arrow-right" />
        </div>
    </div>
</template>
<script>
import Quadrant from './components/Quadrant'
import QuadrantsLimits from './components/QuadrantsLimits'
import AxisLimitsValidations from './components/AxisLimitsValidations'
import { range, times, constant } from 'lodash'

export default {
    components: {
        Quadrant,
        QuadrantsLimits,
        AxisLimitsValidations,
    },
    props: {
        axisXTitle: {
            type: String,
            default: '',
        },
        axisYTitle: {
            type: String,
            default: '',
        },
        rows: {
            type: [Number],
            default: 0,
        },
        rowsState: {
            type: Boolean,
            default: true,
        },
        columns: {
            type: [Number],
            default: 0,
        },
        columnsState: {
            type: Boolean,
            default: true,
        },
        quadrants: {
            type: Array,
            default: () => [],
        },
        quadrantsValidations: {
            type: Array,
            default: () => [],
        },
        limits: {
            type: Object,
            required: true,
        },
        limitsValidations: {
            type: Object,
            required: true,
        },
        targetQuadrantValidation: {
            type: Boolean,
            default: null,
        },
    },
    computed: {
        showQuadrants() {
            return this.rowsState && this.columnsState
        },
    },
    created() {
        this.setQuadrants()
    },
    watch: {
        rows: function() {
            if (this.rowsState) {
                this.setQuadrants()
            }
        },
        columns: function() {
            if (this.columnsState) {
                this.setQuadrants()
            }
        },
        quadrants: function() {
            this.setQuadrantsLimits()
        },
    },
    methods: {
        getLimitValidation(axis, index) {
            const value = this.limitsValidations[axis][index]
            return value ? value.isValid : null
        },
        onUpdateLimit(limitIndex, limitAxis, value) {
            const axis = limitAxis + '_axis'
            const minAxis = limitAxis + '_min'
            const maxAxis = limitAxis + '_max'

            this.updateQuadrantsLimits(
                limitIndex,
                axis,
                minAxis,
                maxAxis,
                value
            )

            this.$emit('update-limit-item', limitIndex, limitAxis, value)
        },
        updateQuadrantsLimits(limitIndex, axis, minAxis, maxAxis, value) {
            if (limitIndex == 0) {
                this.quadrants
                    .filter(q => q[axis] == 1)
                    .forEach(q => {
                        this.updateQuadrantProperty(
                            this.quadrants.indexOf(q),
                            minAxis,
                            value
                        )
                    })
            } else {
                this.quadrants.forEach((quadrant, index) => {
                    if (quadrant[axis] == limitIndex) {
                        this.updateQuadrantProperty(index, maxAxis, value)
                    } else if (quadrant[axis] == limitIndex + 1) {
                        this.updateQuadrantProperty(index, minAxis, value)
                    }
                })
            }
        },
        setQuadrantsLimits() {
            const limits = {
                x: times(this.rows + 1, constant(0)),
                y: times(this.columns + 1, constant(0)),
            }

            ;['x', 'y'].forEach(key => {
                this.limits[key].forEach((v, i) => {
                    if (i < limits[key].length) {
                        limits[key][i] = v
                    }
                })
            })

            this.$emit('update:limits', limits)
        },
        setQuadrants() {
            const quadrants = []

            range(1, this.rows + 1).forEach(row => {
                range(1, this.columns + 1).forEach(column => {
                    const index = this.getQuadrantIndex(row, column)
                    if (index !== -1) {
                        quadrants.push({ ...this.quadrants[index] })
                    } else {
                        quadrants.push({
                            id: Math.random(),
                            title: '(X:' + row + ' Y:' + column + ')',
                            description: null,
                            x_axis: row,
                            y_axis: column,
                            x_min: 0,
                            x_max: 0,
                            y_min: 0,
                            y_max: 0,
                        })
                    }
                })
            })

            this.$emit('update:quadrants', quadrants)
        },
        getQuadrantIndex(row, column) {
            const quadrant = this.quadrants.find(q => {
                return q.x_axis == row && q.y_axis == column
            })

            if (quadrant) {
                return this.quadrants.indexOf(quadrant)
            }

            return -1
        },
        updateQuadrantProperty(index, property, value) {
            this.$emit('update-quadrant', index, property, value)
        },
    },
}
</script>
<style scoped lang="sass">
$background-color: #edf0f1
$container-height-width: 25px
$text-color: #73818f
$font-weight: 600
$icon-font-size: 24px
$axis-title-font-size: 15px

.axis
    color: $text-color
    background-color: $background-color

.axis__icon
    font-size: $icon-font-size

.axis__title
    font-size: $axis-title-font-size
    font-weight: $font-weight

.axis__y
    margin-right: 10px
    width: $container-height-width
    text-align: center
    .axis__title
        text-orientation: inherit
        writing-mode: vertical-lr
        transform: rotate(180deg)
    .axis__icon
        margin-bottom: 10px

.axis__x
    display: flex
    justify-content: flex-end
    width: 100%
    text-align: right
    margin-bottom: 1rem
    height: $container-height-width
    .axis__title
        margin-right: 10px

.quadrants
    width: 100%
    display: flex
    margin-bottom: 0.5rem
    flex-direction: column-reverse

.axis-y-limits
    height: 226px
    padding-left: 13px
</style>
